import React, { useCallback, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePageVisibilityContext } from '../contexts/PageVisibilityContext';
import {
  Dashboard as DashboardIcon,
  Description as ContractsIcon,
  Assessment as AuditIcon,
  AccountBalance as TransactionsIcon,
  TrendingUp as ForecastIcon,
  People as AccountsIcon,
  Settings as SettingsIcon,
  Group as UsersIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Explore as ExploreIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { Summarize as ReportsIcon } from '@mui/icons-material';
import {
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import '../styles/Dashboard.css';
import ContractSelectionModal from './ContractSelectionModal';
import UserDropdown from './UserDropdown';

const MainLayout = ({ 
  children, 
  title, 
  onRefresh, 
  showRefreshButton = false // Default to hiding the refresh button
}) => {
  const { user } = useAuth();
  const location = useLocation();
  const { refreshData } = usePageVisibilityContext();
  const [showContractModal, setShowContractModal] = useState(false);
  
  // Helper function to check if a path is active
  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Safely get the user's initial for the avatar
  const getUserInitial = () => {
    if (user?.firstName && user.firstName.length > 0) {
      return user.firstName[0].toUpperCase();
    }
    if (user?.email && user.email.length > 0) {
      return user.email[0].toUpperCase();
    }
    return 'U'; // Default fallback
  };
  
  // Handle manual refresh click
  const handleRefresh = useCallback(() => {
    // Use provided refresh function if available
    if (typeof onRefresh === 'function') {
      onRefresh();
    } else {
      // Otherwise broadcast refresh to all subscribers
      refreshData();
    }
  }, [onRefresh, refreshData]);

  // Determine if we should show the refresh button
  const shouldShowRefreshButton = showRefreshButton || typeof onRefresh === 'function';

  // Handle explore click to show contract modal
  const handleExploreClick = (e) => {
    e.preventDefault();
    setShowContractModal(true);
  };

  // Handle contract selection from modal
  const handleContractSelect = (contractId) => {
    setShowContractModal(false);
    // Navigate to the client dashboard view for the selected contract
    window.location.href = `/client-view/${contractId}/dashboard`;
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowContractModal(false);
  };

  return (
    <div className="dashboard-container">
      {/* Sidebar Navigation */}
      <nav className="sidebar">
        <div className="sidebar-logo">
          <img src="/logo.png" alt="Logo" height="40" />
        </div>
        
        {/* Contract Management Section */}
        <div className="sidebar-section">
          <div className="sidebar-category">Contract Management</div>
          <Link to="/admin" className={`sidebar-item ${isActive('/admin') ? 'active' : ''}`}>
            <DashboardIcon className="sidebar-item-icon" />
            Dashboard
          </Link>
          <Link to="/contracts" className={`sidebar-item ${isActive('/contracts') ? 'active' : ''}`}>
            <ContractsIcon className="sidebar-item-icon" />
            Contracts
          </Link>
          <Link to="/audit" className={`sidebar-item ${isActive('/audit') ? 'active' : ''}`}>
            <AuditIcon className="sidebar-item-icon" />
            Audit Logs
          </Link>
          <Link to="/reports" className={`sidebar-item ${isActive('/reports') ? 'active' : ''}`}>
            <ReportsIcon className="sidebar-item-icon" />
            Reports
          </Link>
          {/* Hide Transactions and Forecast from team members */}
          {user?.role !== 'team_member' && (
            <>
              <Link to="/transactions" className={`sidebar-item ${isActive('/transactions') ? 'active' : ''}`}>
                <TransactionsIcon className="sidebar-item-icon" />
                Transactions
              </Link>
              <Link to="/forecast" className={`sidebar-item ${isActive('/forecast') ? 'active' : ''}`}>
                <ForecastIcon className="sidebar-item-icon" />
                Forecast
              </Link>
            </>
          )}
        </div>
        
        {/* Account Management Section */}
        <div className="sidebar-section">
          <div className="sidebar-category">Account Management</div>
          <Link to="/points-dashboard" className={`sidebar-item ${isActive('/points-dashboard') ? 'active' : ''}`}>
            <DashboardIcon className="sidebar-item-icon" />
            Dashboard
          </Link>
          <Link to="/performance" className={`sidebar-item ${isActive('/performance') ? 'active' : ''}`}>
            <AccountsIcon className="sidebar-item-icon" />
            Performance
          </Link>
          <a href="#" onClick={handleExploreClick} className="sidebar-item">
            <ExploreIcon className="sidebar-item-icon" />
            Explore
          </a>
          <Link to="/status" className={`sidebar-item ${isActive('/status') ? 'active' : ''}`}>
            <CheckCircleIcon className="sidebar-item-icon" />
            Status
          </Link>
        </div>
        
        {/* Settings Section */}
        <div className="sidebar-section">
          <div className="sidebar-category">Settings</div>
          {/* Hide Users management from team members */}
          {user?.role !== 'team_member' && (
            <Link to="/users" className={`sidebar-item ${isActive('/users') ? 'active' : ''}`}>
              <UsersIcon className="sidebar-item-icon" />
              Users
            </Link>
          )}
          {/* Team members can view syncing status but cannot trigger syncs */}
          <Link to="/syncing" className={`sidebar-item ${isActive('/syncing') ? 'active' : ''}`}>
            <SyncIcon className="sidebar-item-icon" />
            Syncing
          </Link>
        </div>
      </nav>

      {/* Main Content Area */}
      <div className="main-content">
        {/* Header */}
        <header className="header">
          <div className="header-left">
            <div className="page-title">{title}</div>
          </div>
          <div className="header-right">
            {/* Manual Refresh Button - Only shown when explicitly enabled */}
            {shouldShowRefreshButton && (
              <Tooltip title="Refresh page data">
                <IconButton
                  onClick={handleRefresh}
                  color="primary"
                  aria-label="refresh"
                  size="medium"
                  sx={{ mr: 2 }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            )}
            <UserDropdown />
          </div>
        </header>

        {/* Page Content */}
        <div className="scrollable-content">
          {children}
        </div>
      </div>

      {/* Contract Selection Modal */}
      {showContractModal && (
        <ContractSelectionModal 
          onClose={handleModalClose}
          onSelectContract={handleContractSelect}
        />
      )}
    </div>
  );
};

export default MainLayout; 